import { AnswerResponseFromJSON } from './api/generated/models/AnswerResponse'
import type { AnswerResponse } from './api'

export type ChatStreamEvent =
  | { type: 'start'; conversationId: string }
  | { type: 'status'; phase: string; text: string }
  | { type: 'chunk'; content: string }
  | { type: 'done'; conversationId: string; result: AnswerResponse }
  | { type: 'error'; code: string; message: string }

export type StreamAnswerInput = {
  query: string
  conversationId?: string | null
}

export type StreamAnswerOptions = {
  baseUrl: string
  acquireToken: () => Promise<string>
  signal?: AbortSignal
  fetchImpl?: typeof fetch
}

export class ChatStreamError extends Error {
  readonly status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'ChatStreamError'
    this.status = status
  }
}

export async function* streamAnswer(
  input: StreamAnswerInput,
  options: StreamAnswerOptions,
): AsyncGenerator<ChatStreamEvent, void, unknown> {
  const fetchFn = options.fetchImpl ?? fetch
  const token = await options.acquireToken()
  const url = `${options.baseUrl.replace(/\/$/, '')}/api/answer/stream`
  const response = await fetchFn(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      query: input.query,
      conversation_id: input.conversationId ?? null,
    }),
    ...(options.signal ? { signal: options.signal } : {}),
  })

  // Ownership/validity of conversation_id is checked before any SSE bytes:
  // an invalid or foreign id is a plain JSON 404/403, never a stream.
  if (!response.ok) {
    const body = await response.text().catch(() => '')
    throw new ChatStreamError(extractDetail(body) || response.statusText, response.status)
  }

  if (response.body === null) {
    throw new ChatStreamError('response body is null', response.status)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      let separatorIndex = buffer.indexOf('\n\n')
      while (separatorIndex !== -1) {
        const rawEvent = buffer.slice(0, separatorIndex)
        buffer = buffer.slice(separatorIndex + 2)
        const event = parseSseEvent(rawEvent)
        if (event !== null) yield event
        separatorIndex = buffer.indexOf('\n\n')
      }
    }
    // Tail flush — the server is expected to terminate events with `\n\n`
    // but we'll handle a stray trailing event for robustness.
    const tail = buffer.trim()
    if (tail !== '') {
      const event = parseSseEvent(tail)
      if (event !== null) yield event
    }
  } finally {
    reader.releaseLock()
  }
}

function parseSseEvent(raw: string): ChatStreamEvent | null {
  // SSE allows multi-line `data:` for one event. Concatenate all data lines
  // and ignore other field types (event, id, retry — we don't use them).
  const lines = raw.split('\n')
  const dataLines: string[] = []
  for (const line of lines) {
    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).replace(/^ /, ''))
    }
  }
  if (dataLines.length === 0) return null
  const payload = dataLines.join('\n')
  try {
    const parsed = JSON.parse(payload) as Record<string, unknown>
    return toChatStreamEvent(parsed)
  } catch {
    return null
  }
}

function toChatStreamEvent(parsed: Record<string, unknown>): ChatStreamEvent | null {
  const type = parsed['type']
  if (type === 'start') {
    return {
      type: 'start',
      conversationId: stringField(parsed, 'conversation_id'),
    }
  }
  if (type === 'status') {
    return {
      type: 'status',
      phase: stringField(parsed, 'phase'),
      text: stringField(parsed, 'text'),
    }
  }
  if (type === 'chunk') {
    return { type: 'chunk', content: stringField(parsed, 'content') }
  }
  if (type === 'done') {
    return {
      type: 'done',
      conversationId: stringField(parsed, 'conversation_id'),
      result: AnswerResponseFromJSON(parsed['result']) as AnswerResponse,
    }
  }
  if (type === 'error') {
    return {
      type: 'error',
      code: stringField(parsed, 'code'),
      message: stringField(parsed, 'message'),
    }
  }
  return null
}

function extractDetail(body: string): string {
  try {
    const parsed = JSON.parse(body) as Record<string, unknown>
    const detail = parsed['detail']
    if (typeof detail === 'string') return detail
  } catch {
    // not JSON — fall through to the raw body
  }
  return body
}

function stringField(obj: Record<string, unknown>, key: string): string {
  const v = obj[key]
  return typeof v === 'string' ? v : ''
}
